<template>
  <div class="space-y-12">
    <h1 class="text-5xl font-medium text-zinc-800">{{ __('Members') }}</h1>

    <div class="space-y-8">
      <SectionHeader :title="__('Company Team Members')">
        <template #icon>
          <Icon class="text-3xl text-primary" name="assignment_ind" is-filled />
        </template>
      </SectionHeader>

      <Invite
        v-if="isRoleCompanyOwner || isRoleCommunityLead || isRoleHrManagement"
        :members="members"
        :invitations="invitations"
        :options="options"
        @invite="handleInviteButtonClick"
      />

      <div class="grid gap-x-4 gap-y-2 rounded-lg bg-white p-6 md:grid-cols-3">

        <template v-if="filters?.search?.show">
          <InputText
              id="search"
              :placeholder="__('Search recruiter')"
              :value="filters?.search?.value"
              clearable
              @update:value="handleSearchUpdate"
          />
        </template>

        <template v-if="filters?.active?.show">
          <InputMultiSelect
              class="z-40"
              :placeholder="__('Status')"
              :values="filters.active.value"
              :options="filters.active.options"
              @update:values="handleStatusUpdate"
          />
        </template>

        <template v-if="filters?.type?.show">
          <InputMultiSelect
              class="z-40"
              :placeholder="__('Recruiter role')"
              :values="filters.type.value"
              :options="filters.type.options"
              @update:values="handleTypeUpdate"
          />
        </template>

      </div>

      <div class="overflow-x-auto rounded-lg bg-white shadow-redesign">
        <table class="w-max min-w-full overflow-x-hidden">
          <thead class="border-b">
            <tr>
              <th class="px-8 py-4 font-medium leading-normal text-zinc-600">
                {{ __('Name') }}
              </th>
              <th
                v-if="isRoleCompanyOwner || isRoleHrManagement"
                class="px-8 py-4 font-medium leading-normal text-zinc-600"
              >
                {{ __('Status') }}
              </th>
              <th class="px-8 py-4 font-medium leading-normal text-zinc-600">
                {{ __('Role') }}
              </th>
              <th class="px-8 py-4 font-medium leading-normal text-zinc-600">
                {{ __('Date') }}
              </th>
              <th
                v-if="isRoleCompanyOwner || isRoleHrManagement"
                class="px-8 py-4 font-medium leading-normal text-zinc-600"
              >
                {{ __('Action') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <Member
              v-for="member in members"
              :key="member.id"
              :member="member"
              :options="options"
              :can-edit="
                isRoleCompanyOwner || isRoleHrManagement
              "
              :is-current-user="member.id === user.id"
              @update="handleUpdateButtonClick"
              @remove="handleRemoveButtonClick(member)"
            />
            <Invitation
              v-for="invitation in invitations"
              :key="invitation.id"
              :invitation="invitation"
              :can-edit="isRoleCompanyOwner || isRoleHrManagement "
              @invite-again="handleInviteAgainButtonClick"
            />
          </tbody>
        </table>

        <MemberDeleteModal
          v-if="isDeleteModalOpen"
          :member="selected"
          @cancel="handleCancelButtonClick"
          @continue="handleContinueButtonClick"
        />
      </div>

      <Pagination
          class="mt-6"
          :page="pagination.page"
          :pages="pagination.total"
          @update:page="handlePageChange"
      />

    </div>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue';
  import { Icon } from '@/components/block/icon';
  import { SectionHeader } from '@/pages/components/section-header';
  import { Invitation } from './components/invitation';
  import { Member } from './components/member';
  import { MemberDeleteModal } from './components/member-delete-modal';
  import { Invite } from './components/invite';
  import { Pagination } from "@/components/block/pagination";
  import axios from "axios";
  import {__} from "@/helpers";
  import {InputMultiSelect} from "@/components/form/input-multi-select";
  import {InputText} from "@/components/form/input-text";
  import {FormSingleselect} from "@/components/global";

  const props = defineProps({
    members: {
      type: Array,
      required: true
    },
    invitations: {
      type: Array,
      required: true
    },
    options: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    role: {
      type: String,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      required: true
    }
  });

  const pagination = ref(props.pagination);

  const isRoleCompanyOwner = computed(() => {
    return props.role === 'company_owner';
  });

  const isRoleCommunityLead = computed(() => {
    return props.role === 'community_lead';
  });

  const isRoleHrManagement = computed(() => {
    return props.role === 'hr_management';
  });
  const members = ref(JSON.parse(JSON.stringify(props.members)));
  const invitations = ref(JSON.parse(JSON.stringify(props.invitations)));
  const filters = ref(props.filters);
  const selected = ref(null);
  const isDeleteModalOpen = ref(false);

  async function handlePageChange(index) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', index);
    window.history.replaceState({}, '', url);

    await fetchMembers(url.searchParams.toString());
  }

  async function fetchMembers(params) {
    const baseUri = route('redesign.company.members.load');
    const uri = params ? `${baseUri}?${params}` : baseUri;

    try {
      const response = await axios.get(uri);

      members.value = response.data.members;
      pagination.value = response.data.pagination;
      filters.value = response.data.filters;

    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(__('An unexpected error has occured. Please try again later.'));
      }
    }

  }

  let debounceTimeout;
  async function handleTypeUpdate(values) {
    const url = new URL(window.location.href);
    url.searchParams.delete('type[]');
    values.forEach((value) => {
      url.searchParams.append('type[]', value.id);
    });
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchMembers(url.searchParams.toString());
  }

  async function handleStatusUpdate(values) {
    const url = new URL(window.location.href);
    url.searchParams.delete('active[]');
    values.forEach((value) => {
      url.searchParams.append('active[]', value.id);
    });
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url);

    await fetchMembers(url.searchParams.toString());
  }

  async function handleSearchUpdate(value) {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(async () => {
      const url = new URL(window.location.href);

      if (!value) {
        url.searchParams.delete('search');
      } else {
        url.searchParams.set('search', value);
      }

      url.searchParams.delete('page');
      window.history.replaceState({}, '', url);

      await fetchMembers(url.searchParams.toString());
    }, 500);
  }
  function handleInviteButtonClick(invitation) {
    invitations.value.push(invitation);
  }

  function handleUpdateButtonClick(member) {
    members.value = members.value.map((item) => {
      if (item.id === member.id) {
        return member;
      }

      return item;
    });
  }

  function handleRemoveButtonClick(member) {
    selected.value = JSON.parse(JSON.stringify(member));
    isDeleteModalOpen.value = true;
  }

  function handleCancelButtonClick() {
    selected.value = null;
    isDeleteModalOpen.value = false;
  }

  function handleContinueButtonClick(member) {
    members.value = members.value.filter((item) => item.id !== member.id);
    isDeleteModalOpen.value = false;
  }

  function handleInviteAgainButtonClick(invitation) {
    invitations.value = invitations.value.map((item) => {
      if (item.id === invitation.id) {
        return invitation;
      }

      return item;
    });
  }
</script>
